'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useAuthStore } from '@/stores/authStore'
import { Eye, EyeOff, UserPlus } from 'lucide-react'
import Link from 'next/link'

export default function InstallerRegisterPage() {
  const router = useRouter()

  const [companyName, setCompanyName] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 12) {
      setError('Passwort muss mindestens 12 Zeichen lang sein')
      return
    }

    if (password !== passwordConfirm) {
      setError('Passwörter stimmen nicht überein')
      return
    }

    setLoading(true)

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, companyName }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Registrierung fehlgeschlagen')
        return
      }

      // Auto-login: set auth state directly
      useAuthStore.setState({
        token: data.token,
        user: {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          company: data.user.company,
          companyId: data.user.companyId,
          role: data.user.role,
          permissions: data.user.permissions ?? [],
        },
        isAuthenticated: true,
      })

      router.push('/installer/dashboard')
    } catch {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-installer-bg">
      {/* Background gradient */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-installer-bg via-installer-bg to-emerald-dark/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-heiz-orange/5 rounded-full blur-3xl" />
      </div>

      {/* Register card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/logo-mark.svg" alt="HeizOS" style={{ height: 48, width: 'auto', display: 'inline-block' }} />
            <h1 className="text-2xl font-bold text-white">
              Heiz<span className="text-heiz-orange">OS</span>
            </h1>
          </div>
          <p className="text-gray-1 text-sm mt-2">Konto erstellen</p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-installer-card border border-installer-border rounded-2xl p-8 shadow-2xl"
        >
          <h2 className="text-xl font-semibold text-white mb-6">Registrieren</h2>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Company Name */}
          <div className="mb-4">
            <label className="block text-sm text-gray-1 mb-1.5">Firmenname</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Meine Firma GmbH"
              required
              className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                         text-white placeholder:text-gray-1/50 focus:border-heiz-orange focus:ring-1
                         focus:ring-heiz-orange/30 outline-none transition-all text-sm"
            />
          </div>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm text-gray-1 mb-1.5">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Max Mustermann"
              required
              className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                         text-white placeholder:text-gray-1/50 focus:border-heiz-orange focus:ring-1
                         focus:ring-heiz-orange/30 outline-none transition-all text-sm"
            />
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm text-gray-1 mb-1.5">E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@firma.de"
              required
              className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                         text-white placeholder:text-gray-1/50 focus:border-heiz-orange focus:ring-1
                         focus:ring-heiz-orange/30 outline-none transition-all text-sm"
            />
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="block text-sm text-gray-1 mb-1.5">Passwort</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 12 Zeichen"
                required
                minLength={12}
                className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                           text-white placeholder:text-gray-1/50 focus:border-emerald focus:ring-1
                           focus:ring-emerald/30 outline-none transition-all text-sm pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-1 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Password Confirm */}
          <div className="mb-6">
            <label className="block text-sm text-gray-1 mb-1.5">Passwort bestätigen</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="Passwort wiederholen"
              required
              minLength={12}
              className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                         text-white placeholder:text-gray-1/50 focus:border-emerald focus:ring-1
                         focus:ring-emerald/30 outline-none transition-all text-sm"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
                       bg-gradient-to-r from-heiz-orange to-[#c43a22] text-white font-semibold
                       hover:from-[#ff6b4a] hover:to-heiz-orange transition-all disabled:opacity-50
                       disabled:cursor-not-allowed shadow-[0_0_20px_rgba(233,76,50,0.3)] text-sm"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Konto erstellen
              </>
            )}
          </button>

          {/* Link to login */}
          <div className="mt-4 text-center">
            <Link
              href="/installer"
              className="text-xs text-gray-1 hover:text-heiz-orange transition-colors"
            >
              Bereits registriert? <span className="text-heiz-orange">Anmelden</span>
            </Link>
          </div>
        </form>

        <p className="text-center text-xs text-gray-1/50 mt-4">
          HeizOS v1.0
        </p>
      </div>
    </div>
  )
}
